import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, Article } from '../utils/api';
import { useAuthStore } from '../stores/auth';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { useAutoSave, useLoadDraft, useClearDraft } from '../hooks/useAutoSave';
import { tooltips } from '../config/tooltips';
import { IconWithTooltip } from '../components/IconWithTooltip';
import { FloatingActions } from '../components/FloatingActions';
import { ConfirmModal } from '../components/ConfirmModal';

export function Edit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [article, setArticle] = useState<Article | null>(null);
  const [title, setTitle] = useState('');
  const [titleZh, setTitleZh] = useState('');
  const [content, setContent] = useState('');
  const [contentZh, setContentZh] = useState('');
  const [slug, setSlug] = useState('');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'unlisted'>('public');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [pendingArticle, setPendingArticle] = useState<Article | null>(null);
  const [contentLang, setContentLang] = useState<'en' | 'zh'>('en');

  const draftKey = `edit-${id}`;
  const draft = useLoadDraft(draftKey);
  const clearDraft = useClearDraft(draftKey);

  // Auto-save draft
  useAutoSave(draftKey, { title, titleZh, content, contentZh, slug, tags, visibility }, !loading);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;

      try {
        const { article } = await api.getArticle(id);
        setArticle(article);

        // Check if there's a local draft
        if (draft && draft.updatedAt > new Date(article.updated_at).getTime()) {
          // Local draft is newer, show modal to ask user
          setPendingArticle(article);
          setShowDraftModal(true);
        } else {
          setTitle(article.title);
          setTitleZh(article.title_zh || '');
          setContent(article.content);
          setContentZh(article.content_zh || '');
          setSlug(article.slug);
          setTags(article.tags);
          setVisibility(article.visibility);
        }
        setStatus(article.status);
      } catch (error) {
        console.error('Failed to fetch article:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Check permissions
  useEffect(() => {
    if (article && user) {
      const canEdit = user.id === article.author_id || user.role === 'admin';
      if (!canEdit) {
        navigate('/dashboard');
      }
    }
  }, [article, user, navigate]);

  const handleSave = async (newStatus?: 'draft' | 'published') => {
    if (!id || !title.trim() || !content.trim()) {
      alert('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const { article: updated } = await api.updateArticle(id, {
        title,
        title_zh: titleZh || undefined,
        content,
        content_zh: contentZh || undefined,
        slug,
        tags,
        visibility,
        status: newStatus || status,
      });

      clearDraft();
      navigate(`/article/${updated.slug}`);
    } catch (error) {
      console.error('Failed to save article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setDeleting(true);
    try {
      await api.deleteArticle(id);
      clearDraft();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete article:', error);
      alert('Failed to delete article');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDiscard = () => {
    if (!article) return;
    clearDraft();
    navigate(`/article/${article.slug}`);
  };

  const handleRestoreDraft = () => {
    if (draft) {
      setTitle(draft.title);
      setTitleZh(draft.titleZh || '');
      setContent(draft.content);
      setContentZh(draft.contentZh || '');
      setSlug(draft.slug);
      setTags(draft.tags);
      setVisibility(draft.visibility);
    }
    setShowDraftModal(false);
    setPendingArticle(null);
  };

  const handleDiscardDraft = () => {
    if (pendingArticle) {
      setTitle(pendingArticle.title);
      setTitleZh(pendingArticle.title_zh || '');
      setContent(pendingArticle.content);
      setContentZh(pendingArticle.content_zh || '');
      setSlug(pendingArticle.slug);
      setTags(pendingArticle.tags);
      setVisibility(pendingArticle.visibility);
      clearDraft();
    }
    setShowDraftModal(false);
    setPendingArticle(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Article</h1>
      </div>

      {/* Floating Action Buttons */}
      <FloatingActions storageKey="edit-actions-position">
        <IconWithTooltip
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
          tooltip={tooltips.discardChanges}
          onClick={() => setShowDiscardModal(true)}
          disabled={saving || deleting}
          variant="secondary"
        />
        <IconWithTooltip
          icon={
            deleting ? (
              <div className="spinner w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )
          }
          tooltip={tooltips.deleteArticle}
          onClick={() => setShowDeleteModal(true)}
          disabled={deleting}
          variant="danger"
        />
        <IconWithTooltip
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
          }
          tooltip={tooltips.saveDraft}
          onClick={() => handleSave('draft')}
          disabled={saving}
          variant="secondary"
        />
        <IconWithTooltip
          icon={
            saving ? (
              <div className="spinner w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            )
          }
          tooltip={tooltips.publish}
          onClick={() => handleSave('published')}
          disabled={saving}
          variant="primary"
        />
      </FloatingActions>

      <div className="space-y-6">
        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <span className={`badge ${status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
            {status === 'published' ? 'Published' : 'Draft'}
          </span>
          {visibility !== 'public' && (
            <span className={`badge ${visibility === 'private' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
              {visibility === 'private' ? 'Private' : 'Unlisted'}
            </span>
          )}
        </div>

        {/* Title */}
        <div className="card p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              English Title
            </label>
            <input
              type="text"
              placeholder="Article title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-3xl font-bold bg-transparent border-0 p-0 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:ring-0 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              中文标题 (Optional)
            </label>
            <input
              type="text"
              placeholder="文章标题..."
              value={titleZh}
              onChange={(e) => setTitleZh(e.target.value)}
              className="w-full text-2xl font-bold bg-transparent border-0 p-0 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:ring-0 focus:outline-none"
            />
          </div>
        </div>

        {/* Settings */}
        <div className="card p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug
              </label>
              <input
                type="text"
                placeholder="article-url-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags
              </label>
              <input
                type="text"
                placeholder="#Blog #Tech"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Visibility
              </label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as typeof visibility)}
                className="input text-sm"
              >
                <option value="public">Public</option>
                <option value="unlisted">Unlisted (not in list)</option>
                <option value="private">Private (only me)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="card overflow-hidden">
          {/* Language tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setContentLang('en')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                contentLang === 'en'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              English Content
            </button>
            <button
              onClick={() => setContentLang('zh')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                contentLang === 'zh'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              中文内容 (Optional)
            </button>
          </div>
          <div className="p-4">
            {contentLang === 'en' ? (
              <MarkdownEditor
                value={content}
                onChange={setContent}
              />
            ) : (
              <MarkdownEditor
                value={contentZh}
                onChange={setContentZh}
              />
            )}
          </div>
        </div>
      </div>

      {/* Discard Changes Modal */}
      <ConfirmModal
        isOpen={showDiscardModal}
        title="Discard Changes"
        message="Are you sure you want to discard all changes? Any unsaved work will be lost."
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="warning"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardModal(false)}
      />

      {/* Delete Article Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Restore Draft Modal */}
      <ConfirmModal
        isOpen={showDraftModal}
        title="Restore Local Draft?"
        message="You have a local draft that is newer than the saved version. Would you like to restore it?"
        confirmText="Restore Draft"
        cancelText="Use Saved Version"
        variant="default"
        onConfirm={handleRestoreDraft}
        onCancel={handleDiscardDraft}
      />
    </div>
  );
}
